/** Main-thread entry for decoding: RAW via LibRaw's own worker, everything else in the decode worker. */
import type { RawFrame } from '../core/types';
import { decodeBytes } from './decode';
import { decodeRaw, type RawOptions } from './decoders/raw';
import { detectFormat } from './formats';

/** Thrown when the worker itself fails (as opposed to a decoding error in a file). */
class WorkerFailure extends Error {}

let worker: Worker | null = null;
/** Set once the worker has failed: decode on the main thread from then on. */
let workerBroken = false;
let seq = 0;
const pending = new Map<number, { resolve: (f: RawFrame) => void; reject: (e: Error) => void }>();

function getWorker(): Worker | null {
  if (workerBroken) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./decode.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<{ id: number; frame?: RawFrame; error?: string }>) => {
      const p = pending.get(e.data.id);
      if (!p) return;
      pending.delete(e.data.id);
      if (e.data.frame) p.resolve(e.data.frame);
      else p.reject(new Error(e.data.error ?? 'Decoding failed'));
    };
    // Script load failure (CSP, hosting) or a crash: fail pending jobs over to the main thread.
    worker.onerror = (ev) => {
      ev.preventDefault?.();
      for (const p of pending.values()) p.reject(new WorkerFailure('Decoder worker failed'));
      pending.clear();
      worker?.terminate();
      worker = null;
      workerBroken = true;
    };
  } catch {
    worker = null;
    workerBroken = true;
  }
  return worker;
}

export async function decodeFile(bytes: Uint8Array, name: string, raw: RawOptions): Promise<RawFrame> {
  if (detectFormat(bytes, name) === 'raw') return decodeRaw(bytes, name, raw);
  const w = getWorker();
  if (!w) return decodeBytes(bytes, name);
  const id = ++seq;
  try {
    return await new Promise<RawFrame>((resolve, reject) => {
      pending.set(id, { resolve, reject });
      // Copy so the caller keeps its bytes (the copy's buffer is transferred).
      const copy = bytes.slice();
      w.postMessage({ id, bytes: copy, name }, [copy.buffer]);
    });
  } catch (e) {
    if (e instanceof WorkerFailure) return decodeBytes(bytes, name);
    throw e;
  }
}

/** Test hook: simulate a worker failure. */
export function _breakWorkerForTests() {
  worker?.terminate();
  worker = null;
  workerBroken = false;
  // A worker whose script does not exist fails exactly like a blocked/unloadable worker.
  worker = new Worker('data:text/javascript,throw new Error("boom")', { type: 'module' });
  worker.onmessage = null;
  worker.onerror = (ev) => {
    ev.preventDefault?.();
    for (const p of pending.values()) p.reject(new WorkerFailure('Decoder worker failed'));
    pending.clear();
    worker = null;
    workerBroken = true;
  };
}
